import {
  Box,
  Chip,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { TrackingColumnsContext } from "contexts/TrackingColumns";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  AutomatedContactProperty,
  OutreachContact,
  OutreachContactStatus,
} from "schemas/dashboard";
import { CustomEvent, SetAlertType } from "schemas/functions";

import OpenedColumn from "components/OpenedColumn";
import OutreachStatusChip from "components/OutreachStatusChip";
import { SelectOption } from "features/Influencer/Tracking/schema";
import { sanitize } from "utils/sanitize";
import { hideEmail } from "utils/string";
import { trackEvent } from "utils/tracking";

import IconAction from "./components/IconAction";
import { getFormattedDate } from "./helpers";
import styles from "./styles";

interface Props {
  contact: OutreachContact;
  setAlert: SetAlertType;
}

const ContactRow = ({ contact, setAlert }: Props) => {
  const formattedDate = getFormattedDate(contact);
  const navigate = useNavigate();
  const { contactProperties } = useContext(TrackingColumnsContext);
  const { discoverTab } = useContext(DiscoverViewContext);

  const theme = useTheme();
  const isTinyScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isSmallScreen =
    isMobileScreen || (isLargeScreen && discoverTab !== null);

  const openEmailDrawer = (event: CustomEvent, contact: OutreachContact) => {
    event.stopPropagation();
    trackEvent("Inbox Table Row Clicked", {
      contactId: contact?.id,
      lastGmailThreadId: contact.lastGmailThreadId,
    });

    const isScheduled =
      !contact.latestSequenceSentCount && contact.latestSequenceNextSendAt;

    let url = contact?.lastGmailThreadId
      ? `${contact.id}/email/${contact.lastGmailThreadId}`
      : isScheduled
        ? `${contact.id}/scheduled`
        : `${contact.id}/email`;

    if (!isTinyScreen) {
      navigate(url);
    } else {
      navigate(`${contact.id}`);
    }
  };

  const participants =
    contact.lastGmailThreadParticipants ||
    `To: ${
      contact.contactName?.split(" ")?.[0] ||
      contact.email?.split("@")?.[0] ||
      ""
    }`;

  const snippet = `${
    contact.lastGmailMessageSnippet
      ? sanitize(contact.lastGmailMessageSnippet).substring(0, 170)
      : ""
  }...`;

  const stageProperty = contactProperties.find(
    (p) => p.automatedProperty === AutomatedContactProperty.stage,
  );

  const nicheProperty = contactProperties.find((p) => p.name === "Niche");

  let stageValue = null;
  let nicheValues = [];

  if (stageProperty?.id && contact.propertyValues?.[stageProperty.id]) {
    const propertyValue = contact.propertyValues?.[stageProperty.id];
    stageValue = stageProperty.selectOptions?.find(
      (option) => option.id === propertyValue,
    );
  }

  if (nicheProperty?.id && contact.propertyValues?.[nicheProperty.id]) {
    const propertyValue = contact.propertyValues[nicheProperty.id];
    const optionsMap = Object.fromEntries(
      (nicheProperty.selectOptions || []).map((opt) => [opt.id, opt]),
    );

    nicheValues = propertyValue
      .map((id: string) => optionsMap[id])
      .filter(Boolean);
  }

  let source = null;
  if (contact.fromInstagramTracking) {
    source = "Instagram Tracking";
  } else if (contact.fromVa) {
    source = "VA";
  } else if (contact.campaignId) {
    source = "Campaign";
  }

  return (
    <Grid
      container
      alignItems="center"
      columnSpacing={1}
      onClick={(event) => openEmailDrawer(event, contact)}
      sx={[styles.accordion, contact.isPinned ? styles.rowPinned : {}]}
    >
      {isSmallScreen && (
        <>
          <Box sx={styles.mobilePinned}>
            <IconAction
              fieldName="isPinned"
              contact={contact}
              setAlert={setAlert}
            />
          </Box>
          <Grid item xs={12} container columnSpacing={1}>
            <Grid item xs>
              <Typography component="div" sx={styles.mobileTitle}>
                <Typography
                  component="span"
                  sx={{
                    mr: 1,
                    fontWeight: contact.isPinned ? 700 : "inherit",
                  }}
                >
                  {contact?.brand?.name}
                </Typography>
                {stageValue && (
                  <OutreachStatusChip
                    sx={{ mb: 0, mr: 1 }}
                    stage={stageValue}
                  />
                )}
                {nicheValues.map((value: SelectOption) => (
                  <Chip
                    size="small"
                    key={value.id}
                    sx={{
                      backgroundColor: value.color,
                      fontSize: 12,
                      borderRadius: 2,
                      color: grey[800],
                      mr: 1,
                      textTransform: "capitalize",
                    }}
                    label={value.name}
                  />
                ))}
                {source && (
                  <Chip
                    size="small"
                    sx={{
                      backgroundColor: grey[300],
                      fontSize: 12,
                      borderRadius: 2,
                      color: grey[800],
                      mr: 1,
                    }}
                    label={`From ${source}`}
                  />
                )}
              </Typography>
              <Grid container alignItems="center">
                <Grid
                  item
                  container
                  alignItems="center"
                  xs={12}
                  sx={{ mr: 1, mb: 0.5 }}
                >
                  <Typography component="div" sx={styles.participants}>
                    {participants}
                  </Typography>
                  {contact.lastGmailThreadLength &&
                    contact.lastGmailThreadLength > 1 && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={styles.lastThreadLength}
                      >
                        {contact.lastGmailThreadLength}
                      </Typography>
                    )}
                </Grid>
              </Grid>
              <Typography sx={styles.subject} gutterBottom component="div">
                {contact?.lastGmailMessageSubject}
              </Typography>
              <Typography sx={styles.email} gutterBottom component="div">
                {formattedDate}
              </Typography>
              <Typography
                component="div"
                dangerouslySetInnerHTML={{
                  __html: snippet,
                }}
                sx={styles.snippet}
              ></Typography>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Box sx={styles.openedColumnMobile}>
              <OpenedColumn contact={contact} />
            </Box>
          </Grid>
        </>
      )}
      {!isSmallScreen && (
        <>
          <Grid item md="auto">
            <IconAction
              fieldName="isPinned"
              contact={contact}
              setAlert={setAlert}
            />
          </Grid>
          <Grid item md="auto">
            <Box sx={styles.nameWrapper}>
              <Box
                component="div"
                sx={{ fontWeight: contact.isPinned ? 700 : "inherit" }}
              >
                {contact?.brand?.name}
              </Box>

              <Typography sx={styles.email}>{contact?.contactName}</Typography>
              <Typography sx={styles.email}>{contact?.contactTitle}</Typography>
              <Typography sx={styles.email}>
                {contact?.status === OutreachContactStatus.pending
                  ? hideEmail(contact.email || "")
                  : contact.email}
              </Typography>
            </Box>
          </Grid>
          <Grid item md>
            <Box component="div">
              <Grid container alignItems="center">
                <Grid
                  item
                  container
                  alignItems="center"
                  xs="auto"
                  sx={{ mr: 1, mb: 0.5 }}
                >
                  <Typography component="div" sx={styles.participants}>
                    {participants}
                  </Typography>
                  {contact.lastGmailThreadLength &&
                    contact.lastGmailThreadLength > 1 && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={styles.lastThreadLength}
                      >
                        {contact.lastGmailThreadLength}
                      </Typography>
                    )}
                </Grid>
                <Grid item xs="auto">
                  {stageProperty && stageValue && (
                    <OutreachStatusChip
                      stage={stageValue}
                      sx={{ mb: 0.5, mr: 1 }}
                    />
                  )}
                </Grid>
                {nicheValues.map((value: SelectOption) => (
                  <Grid key={value.id} item xs="auto">
                    <Chip
                      size="small"
                      sx={{
                        backgroundColor: value.color,
                        fontSize: 12,
                        borderRadius: 2,
                        color: grey[800],
                        mb: 0.5,
                        mr: 1,
                        textTransform: "capitalize",
                      }}
                      label={value.name}
                    />
                  </Grid>
                ))}
                {source && (
                  <Grid key="source" item xs="auto">
                    <Chip
                      size="small"
                      sx={{
                        backgroundColor: grey[300],
                        fontSize: 12,
                        borderRadius: 2,
                        color: grey[800],
                        mb: 0.5,
                        mr: 1,
                      }}
                      label={`From ${source}`}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
            <Typography sx={styles.subject}>
              {contact?.lastGmailMessageSubject}
            </Typography>

            <Typography
              dangerouslySetInnerHTML={{
                __html: snippet,
              }}
              sx={styles.snippet}
            ></Typography>
          </Grid>

          <Grid item md="auto">
            <OpenedColumn contact={contact} />
          </Grid>
          <Grid item md="auto">
            <Typography
              variant="caption"
              color="textSecondary"
              sx={styles.formattedDate}
              component="div"
            >
              {formattedDate}
            </Typography>
          </Grid>
        </>
      )}
    </Grid>
  );
};
export default ContactRow;
